import React from 'react'
import './sec.css'
import girlImg from './secondaryassets/newGreen.png'
import lighticon from '../../assets/FINALLIGHTLOGO.svg'
import { Helmet } from 'react-helmet-async'

export default function Aboutus({ selectedLanguage }) {
  const translations = {
    english: {
      opening: "We are Sitemanifest.",
      tagline: "The Team Behind High-Integrity Verified Reviews.",
      team_origin: "We're a dedicated team of individuals from Georgia, driven by passion in everything we do.",
      hero_banner: "Customer Happiness is Your Fastest Path to Increased Sales and Brand Loyalty",
      whatwedo: "What we do",
      whatwedo_desc: "We unite consumers and companies through a high-velocity trust protocol. Sitemanifest eliminates the 'signup wall'—the place where 70% of reviews go to die. With our Guest-First Protocol, invited customers can provide verified proof in seconds, not minutes. We don't just capture feedback; we turn private praise from WhatsApp and DMs into liquid social proof that fuels your conversion rate 24/7",
      protocol_title: "The Sitemanifest Protocol",
      p1_title: "Identity-First",
      p1_desc: "No anonymous noise. We bridge the gap between 'user' and 'actual customer.' Every review on Sitemanifest is anchored to a documented interaction, ensuring that every voice belongs to someone who actually paid for the experience.",
      p2_title: "Proof-of-Interaction",
      p2_desc: "The 'open review' flaw ends here. We are building the gateway that requires documented proof of service.",
      p3_title: "Zero Noise",
      p3_desc: "By blocking scrapers and bots, we maintain a private, high-integrity repository of truth.",
      manifesto_quote: "Starting my own small business, I immediately faced the challenge of demonstrating social proof and credibility. It was too difficult to earn trust and showcase genuine customer success. That realization inspired Sitemanifest: a simple, effective platform to centralize authentic Reviews and help every company, big or small, build the undeniable reputation they deserve.",
      status_label: "Network Status",
      status_value: "Alpha Phase / Genesis",
    }
  };

  const t = translations[selectedLanguage] || translations.english;

  return (
    <div className='flex flex-col overflow-hidden w-full bg-[#f3f3f3]'>

      <script type="application/ld+json">
  {JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://sitemanifest.com/#organization",
        "name": "Sitemanifest",
        "url": "https://sitemanifest.com",
        "logo": "https://sitemanifest.com/static/media/FINALDARKLOGO.c6c0cf71b299ff53c45dfa12750037b2.svg", 
        "description": "A high-velocity trust protocol and review platform based in Georgia.",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Tbilisi",
          "addressCountry": "GE"
        },
        "founder": {
          "@id": "https://sitemanifest.com/about-us/#founder"
        }
      },
      {
        "@type": "Person",
        "@id": "https://sitemanifest.com/about-us/#founder",
        "name": "Omar Getiashvili",
        "jobTitle": "CEO & Architect",
        "affiliation": {
          "@id": "https://sitemanifest.com/#organization"
        },
        "description": "Founder of Sitemanifest, focused on building identity-first review protocols and social proof systems."
      }
    ]
  })}
</script>

<Helmet>
  <title>About Sitemanifest | Our Mission for Verified Reviews</title>
  
  <meta 
    name="description" 
    content="Sitemanifest is on a mission to redefine online trust. Based in Georgia, we are building a high-integrity protocol for verified reviews and customer testimonials to eliminate fake feedback." 
  />

  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://sitemanifest.com/about-us" />

  <meta property="og:title" content="About Sitemanifest | The Future of Verified Reviews" />
  <meta property="og:description" content="Meet the team building the Guest-First Protocol to bridge the gap between businesses and real customers." />

  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Sitemanifest | Built for Verified Reviews" />
  <meta name="twitter:description" content="Discover the protocol behind the world's most transparent testimonial platform." />
</Helmet>
      
      {/* 1. BRAND OPENER & HERO */}
      <section className="w-full pt-[50px] lg:pt-[75px] px-[16px] xl:pt-[50px] 2xl:pt-[120px] xs:px-[32px] mt-[30px] 2xl:px-[64px] xl:px-[64px] 2xl:max-w-[1440px] 2xl:m-auto xl:mt-[80px]">
        <div className="lg:px-0 flex flex-col">
          <h2 className="font-noto text-[24px] text-[#111] tracking-[3px] font-black xs:text-[32px] lg:text-[42px] xl:text-[64px] uppercase italic leading-none">
            {t.opening}
          </h2>
          <h1 className="font-noto mt-[24px] text-[12px] text-[#08696D] tracking-[2px] font-bold xs:text-[16px] lg:text-[20px] uppercase">
            {t.tagline}
          </h1>
        </div>

        <div className="relative w-full mt-[40px]">
          <div className="w-full h-[285px] lg:h-[450px] bg-[#08696D] relative rounded-[2rem] shadow-2xl z-5 flex items-center lg:p-16 p-8">
            <p className="font-inter text-white z-10 leading-[48px] lg:leading-[72px] 2xl:leading-[85px] font-bold text-[24px] lg:text-[50px] max-w-[280px] xs:max-w-[450px] lg:max-w-[700px]">
              {t.hero_banner}
            </p>
          </div>
          <img 
            className="absolute bottom-0 -right-10 z-9 w-[280px] lg:w-[450px] origin-bottom" 
            src={girlImg} 
            alt="Sitemanifest representing customer happiness and trust" 
            style={{ bottom: '0px' }}
          />
        </div>
      </section>

      {/* 2. THE MISSION STATEMENT */}
      <section className='w-full px-[16px] xs:px-[32px] mt-[80px] 2xl:px-[64px] 2xl:max-w-[1440px] 2xl:mx-auto'>
        <div className='flex flex-col gap-[10px] mb-20'> 
          <h2 className='font-spartan text-[26px] text-[#111] tracking-[2px] font-black uppercase'>{t.whatwedo}</h2>
          <p className='font-jakarta text-slate-600 text-[16px] lg:text-[20px] leading-relaxed max-w-4xl'>
            {t.whatwedo_desc}
          </p>
        </div>
      </section>

      {/* 3. THE MANIFESTO SECTION (CEO QUOTE) */}
      <section className='w-full bg-[#111] py-24 relative overflow-hidden'>
        <div className='absolute inset-0 opacity-10 pointer-events-none' 
             style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
        
        <div className='max-w-4xl mx-auto px-6 relative z-10'>
          <img className='w-12 mb-10' src={lighticon} alt="Sitemanifest Logo" />
          <blockquote className='text-white font-noto text-xl lg:text-2xl font-light italic leading-relaxed mb-12 border-l-4 border-[#08696D] pl-8'>
            "{t.manifesto_quote}"
          </blockquote>
          <div className='flex items-center gap-6'>
            <div className='w-16 h-1 bg-[#08696D]' />
            <div className='text-white'>
               <p className='font-bold text-lg uppercase tracking-widest'>Omar Getiashvili</p>
               <p className='text-xs opacity-50 uppercase tracking-widest italic'>CEO / Architect of Sitemanifest</p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. THE CORE PROTOCOL (Grid Hierarchy Fixed) */}
      <section className='py-20 bg-white'>
        <div className='max-w-7xl mx-auto px-6'>
          <h2 className='font-staat text-3xl tracking-[10px] text-center mb-16 text-slate-300'>{t.protocol_title}</h2>
          <div className='grid md:grid-cols-3 gap-12'>
            {[
              { title: t.p2_title, desc: t.p2_desc, icon: "ID_V" },
              { title: t.p1_title, desc: t.p1_desc, icon: "PR_F" },
              { title: t.p3_title, desc: t.p3_desc, icon: "ZN_T" }
            ].map((item, i) => (
              <article key={i} className='group p-8 rounded-3xl hover:bg-slate-50 transition-all'>
                <h3 className='font-spartan text-xl font-black mt-4 mb-4 uppercase tracking-tighter'>{item.title}</h3>
                <p className='text-slate-500 leading-relaxed text-sm'>{item.desc}</p>
              </article>
            ))}
          </div>
        </div>
      
      <div className='w-full flex items-center justify-center mt-[44px]'>
       <a href="https://postyourstartup.co/startup/sitemanifest?ref=badge" target="_blank">
        <img src="https://postyourstartup.co/api/badge/sitemanifest?theme=dark" alt="Featured on PostYourStartup" width="212" height="55" />
      </a>
    </div>
      <div className='w-full flex items-center justify-center mt-[44px]'>
    <a href="https://startupfa.me/s/sitemanifest?utm_source=sitemanifest.com" target="_blank"><img src="https://startupfa.me/badges/featured-badge.webp" alt="sitemanifest.com - Featured on Startup Fame" width="171" height="54" /></a>
  
    </div>



      </section>
  
    </div>
  )
}